import { type RowSelectionState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SortBy } from '../../../interfaces/sort-by.interface';
import { LoadingComponent } from '../../loading/loading.component';
import { TableComponent } from '../../table/table.component';
import { AppTabularDto } from '../dtos/app-tabular.dto';
import { DataTablePaginatedComponentProps } from './data-table-basic.interface';
export interface DataTableFlatComponentProps
  extends DataTablePaginatedComponentProps {
  data: AppTabularDto<any> | undefined;
}

const defCols: any[] = [];
const defRows: any[] = [];
/**
 * This table component renders tabular data.
 */
export const DataTableFlatComponent = ({
  onRowSelectionChange,
  enableRowSelection = false,
  enableMultiRowSelection,
  height,
  parseMeasuresColumns,
  parseColumns,
  startColumns,
  endColumns,
  showTotal = false,
  className,
  idField,
  data,
  totalData,
  isFetching,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  setSortBy,
  sortBy,
  enableStickyFirstColumn = false,
  suppressRowClickSelection,
  expandRows,
}: DataTableFlatComponentProps) => {
  const handler = useCallback(
    (sortByPar: SortBy[] | undefined) => {
      if (sortByPar && sortByPar?.length > 0) {
        setSortBy(sortByPar);
      }
    },
    [setSortBy],
  );

  const total = useMemo(() => {
    return totalData?.rows[0];
  }, [totalData]);
  const columnGroups = useMemo(() => {
    if (totalData && totalData.columnGroups) {
      return totalData.columnGroups;
    }
  }, [totalData]);
  const cols = useMemo(() => {
    if (totalData && totalData.columns) {
      return totalData.columns;
    }
  }, [totalData]);
  const measuresCols = useMemo(() => {
    if (totalData && totalData.measureColumns) {
      return totalData.measureColumns;
    }
  }, [totalData]);

  const columns = useMemo(() => {
    if (cols && measuresCols) {
      const dimCols = parseColumns(cols, data, total, columnGroups) ?? defCols;
      const figureCols =
        parseMeasuresColumns(measuresCols, data, total, columnGroups) ??
        defCols;
      let finalCols: any[] = [];
      finalCols = dimCols.concat(figureCols);
      if (startColumns) {
        finalCols = startColumns.concat(finalCols);
      }
      if (endColumns) {
        finalCols = finalCols.concat(endColumns);
      }
      return finalCols;
    } else {
      return defCols;
    }
  }, [
    columnGroups,
    cols,
    measuresCols,
    parseColumns,
    parseMeasuresColumns,
    startColumns,
    endColumns,
    data,
    total,
  ]);

  const flatData = useMemo(() => data?.rows ?? defRows, [data?.rows]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  useEffect(() => {
    if (onRowSelectionChange && idField) {
      const allRows = flatData.flatMap((i) => i.children ?? [i]);

      let found = allRows.filter(
        (i) => rowSelection && rowSelection[i[idField]] === true,
      );

      if (found.length === 0) {
        found = allRows.filter(
          (i) => rowSelection && rowSelection[i.stableKey] === true,
        );
      }

      if (found.length === 0) {
        found = allRows.filter(
          (i) => rowSelection && rowSelection[i.id] === true,
        );
      }

      onRowSelectionChange(found);
    }
  }, [rowSelection, flatData, onRowSelectionChange, idField]);

  return (
    <LoadingComponent isLoading={isFetching && !isFetchingNextPage}>
      <TableComponent
        className={className}
        showTotal={showTotal}
        height={height}
        defaultSortBy={sortBy}
        data={flatData}
        columns={columns}
        loadMore={hasNextPage}
        getMore={fetchNextPage ? fetchNextPage : () => null}
        onSortBy={handler}
        idField={idField}
        onRowSelectionChange={setRowSelection}
        rowSelection={rowSelection}
        enableRowSelection={enableRowSelection}
        enableMultiRowSelection={enableMultiRowSelection}
        enableStickyFirstColumn={enableStickyFirstColumn}
        suppressRowClickSelection={suppressRowClickSelection}
        expandRows={expandRows}
      />
    </LoadingComponent>
  );
};
